import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { generateConfig } from '../../utils/api';
import { handleApiError } from '../../utils/apiErrorHandler';
import {
  setLoading,
  setSending,
  setError,
  setConversations,
  setMessages,
  addMessage,
  markMessagesAsRead,
  setUnreadCount
} from '../slices/chatSlice';

// Fetch conversations
export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async ({ page = 1, limit = 20, search = '' }, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/chat/conversations`, {
        ...config,
        params: { page, limit, search }
      });

      if (response.data.success) {
        dispatch(setConversations(response.data.data));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch conversations');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Fetch messages for a conversation
export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async ({ conversationId, page = 1, limit = 50 }, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/chat/conversations/${conversationId}/messages`, {
        ...config,
        params: { page, limit }
      });

      if (response.data.success) {
        dispatch(setMessages({
          conversationId,
          messages: response.data.data.messages,
          pagination: response.data.data.pagination
        }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch messages');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Send a message
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ conversationId, message }, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setSending(true));
      dispatch(setError(null));

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(`${baseURL}/api/chat/conversations/${conversationId}/messages`, {
        message
      }, config);

      if (response.data.success) {
        // Add the sent message to the conversation
        dispatch(addMessage({
          conversationId,
          message: response.data.data.message
        }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    } finally {
      dispatch(setSending(false));
    }
  }
);

// Mark messages as read
export const markConversationMessagesAsRead = createAsyncThunk(
  'chat/markMessagesAsRead',
  async ({ messageIds, conversationId }, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.put(`${baseURL}/api/chat/messages/read`, {
        messageIds,
        conversationId
      }, config);

      if (response.data.success) {
        dispatch(markMessagesAsRead({ conversationId, messageIds }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to mark messages as read');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch unread count
export const fetchUnreadCount = createAsyncThunk(
  'chat/fetchUnreadCount',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/chat/unread-count`, config);

      if (response.data.success) {
        dispatch(setUnreadCount(response.data.data.count));
        return response.data.data.count;
      } else {
        throw new Error(response.data.message || 'Failed to fetch unread count');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Find conversation by client or phone
export const findConversation = createAsyncThunk(
  'chat/findConversation',
  async ({ clientId, phoneNumber }, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const params = {};
      if (clientId) params.clientId = clientId;
      if (phoneNumber) params.phoneNumber = phoneNumber;

      const response = await axios.get(`${baseURL}/api/chat/conversations/find`, {
        ...config,
        params
      });

      if (response.data.success) {
        return response.data.data.conversation;
      } else {
        throw new Error(response.data.message || 'Conversation not found');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Search conversations
export const searchConversations = createAsyncThunk(
  'chat/searchConversations',
  async ({ query }, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/chat/conversations`, {
        ...config,
        params: { search: query, page: 1, limit: 50 }
      });

      if (response.data.success) {
        dispatch(setConversations(response.data.data));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Search failed');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Create new conversation
export const createNewConversation = createAsyncThunk(
  'chat/createNewConversation',
  async ({ clientId, phoneNumber }, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(`${baseURL}/api/chat/conversations`, {
        clientId,
        phoneNumber
      }, config);

      if (response.data.success) {
        // Refresh conversations list to include the new one
        dispatch(fetchConversations({ page: 1, limit: 50 }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create conversation');
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export default {
  fetchConversations,
  fetchMessages,
  sendMessage,
  markConversationMessagesAsRead,
  fetchUnreadCount,
  findConversation,
  searchConversations,
  createNewConversation
};